/**
 * Configuration for routing relationship sections to their appropriate getter functions
 * This replaces the long if/else chains and makes it easier to add new entity types
 */

/**
 * Creates a routing configuration for relationship sections
 * @param {Object} getters - All the getter functions
 * @returns {Object} Routing configuration
 */
const createRelationshipRouting = (getters) => {
  return {
    // Interface-specific getters (CANDIDATE FOR EXTRACTION)
    interface_provided_by_app: () => getters.getInterfaceProvidedByApp(),
    interface_consumed_by_app: () => getters.getInterfaceConsumers(),
    // interface_provided_by_sc / interface_consumed_by_sc retired with SC.
    interface_transfers_data: () => getters.getInterfaceTransferredData(),
    interface_affected_by_initiative: () =>
      getters.getInterfaceAffectedByInitiative(),
    // technology_component_used_by_interface is handled conditionally based on entity type
    // For technology components: use getInterfacesImplementedByTechnologyComponent
    // For interfaces: use getInterfaceImplementedByITComponents (backward compatibility)
    technology_component_used_by_interface: () => {
      // This will be overridden in EntityRelationships based on entity type
      return getters.getInterfacesImplementedByTechnologyComponent?.() || getters.getInterfaceImplementedByITComponents?.() || [];
    },

    // Data object-specific getters (CANDIDATE FOR EXTRACTION)
    data_object_transferred_by_interface: () =>
      getters.getDataObjectTransferredByInterface(),
    data_object_affected_by_initiative: () =>
      getters.getDataObjectAffectedByInitiative(),

    // Technology component-specific getters (incoming relationships)
    initiative_affects_it_reverse: () =>
      getters.getInitiativesAffectingTechnologyComponent(),
    it_provided_by_provider_reverse: () =>
      getters.getProvidersOfferingTechnologyComponent(),
    technology_component_used_by_interface: () =>
      getters.getInterfacesImplementedByTechnologyComponent(),

    // Provider-specific getters
    technology_component_offered_by_provider: () =>
      getters.getTechnologyComponentsOfferedByProvider(),
    provider_supports_initiative: () =>
      getters.getProviderSupportsInitiative(),

    // Application-specific getters (incoming relationships)
    organization_uses_application_reverse: () =>
      getters.getOrganizationsUsingThisApplication(),

    // Standard interface getters
    provided_interfaces: () => getters.getProvidedInterfaces(),
    consumed_interfaces: () => getters.getConsumedInterfaces(),

    // Generic entity type getters (can be generalized)
    related_organizations: () => getters.getRelatedOrganizations(),
    related_initiatives: () => getters.getRelatedInitiatives(),
    related_business_capabilities: () =>
      getters.getRelatedBusinessCapabilities(),
    related_business_contexts: () => getters.getRelatedBusinessContexts(),
    related_data_objects: () => getters.getRelatedDataObjects(),
    related_it_components: () => getters.getRelatedITComponents(),
    related_applications: () => getters.getRelatedApplications(),
    related_business_actors: () => getters.getRelatedBusinessActors(),
    related_providers: () => getters.getRelatedProviders(),
    related_goals: () => getters.getRelatedGoals(),

    // Special getters
    related_successors: () => getters.getRelatedSuccessors(),

    // Parent getters (dynamic based on entity type)
    parentGetters: (entityType) => () => getters.getRelatedParents(entityType),
  };
};

/**
 * Get the appropriate getter function for a section
 */
const getGetterForSection = (sectionKey, routing, entityType = null) => {
  // Handle parent relationships dynamically
  if (sectionKey.includes("_parents") && entityType) {
    return routing.parentGetters(entityType);
  }

  // Direct lookup for predefined sections
  const getter = routing[sectionKey];
  if (getter) {
    return getter;
  }

  // Fallback to generic form-based getter
  return null;
};

/**
 * Determine if a section is incoming or outgoing
 */
const isIncomingRelationship = (sectionKey) => {
  const incomingSections = [
    "interface_provided_by_app",
    "interface_consumed_by_app",
    "interface_affected_by_initiative",
    "data_object_transferred_by_interface",
    "data_object_affected_by_initiative",
    "initiative_affects_it_reverse",
    "it_provided_by_provider_reverse",
    "technology_component_used_by_interface",
    "organization_uses_application_reverse",
  ];

  return incomingSections.includes(sectionKey);
};

/**
 * Configuration for validation rules per section
 */
const sectionValidationRules = {
  interface_provided_by_app: {
    maxItems: 1,
    minItems: 1,
    message: "Only one application can provide this interface.",
    minMessage:
      "Select the application that provides this interface.",
  },
  interface_consumed_by_app: {
    minItems: 1,
    minMessage:
      "Select at least one application that consumes this interface.",
  },
  related_organization_parents: {
    maxItems: 1,
    message: "Only one parent is allowed.",
  },
  related_initiative_parents: {
    maxItems: 1,
    message: "Only one parent is allowed.",
  },
  related_businesscontext_parents: {
    maxItems: 1,
    message: "Only one parent is allowed.",
  },
  related_businesscapability_parents: {
    maxItems: 1,
    message: "Only one parent is allowed.",
  },
  related_applicationinterface_parents: {
    maxItems: 1,
    message: "Only one parent is allowed.",
  },
};

/**
 * Get validation rule for a section
 */
export const getValidationRule = (sectionKey) => {
  // Check specific section
  if (sectionValidationRules[sectionKey]) {
    return sectionValidationRules[sectionKey];
  }

  // Check for parent sections
  if (sectionKey.includes("_parents")) {
    return { maxItems: 1, message: "Only one parent is allowed." };
  }

  return null;
};

/**
 * Section key to related entity type mapping
 */
const sectionToEntityType = {
  related_organizations: "organization",
  related_initiatives: "initiative",
  related_business_capabilities: "business-capability",
  related_applications: "application",
  related_it_components: "technology-component",
  related_business_actors: "business-actor",
  related_data_objects: "data-object",
  related_business_contexts: "business-context",
  related_providers: "provider",
  related_goals: "objective",
  provided_interfaces: "interface",
  consumed_interfaces: "interface",
  interface_affected_by_initiative_reverse: "interface",
  interface_provided_by_app: "application",
  interface_consumed_by_app: "application",
  interface_affected_by_initiative: "initiative",
  data_object_transferred_by_interface: "interface",
  data_object_affected_by_initiative: "initiative",
  initiative_affects_it_reverse: "initiative",
  it_provided_by_provider_reverse: "provider",
  technology_component_used_by_interface: "interface",
  technology_component_offered_by_provider: "technology-component",
  provider_supports_initiative: "initiative",
  organization_uses_application_reverse: "organization",
};
